import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import { countryAtom } from '@/atoms/countryState';

import { tokenUpdate } from '../../atoms/authModalControl';
import { cartAtom } from '../../atoms/Cart';
import { localCart } from '../../atoms/LocalCart';
import { localUserID, user } from '../../atoms/user';
import { formatCartData } from '../../components/CartPage';
import decryptToken from '../../components/shared/crypto/decryptToken';
import { formateErrorObject } from '../../components/shared/formateErrorObject';
import {
  getUserId,
  getUserName,
  getZl_UID,
  removeUserId,
  removeZl_UID,
  setToken,
  setUserId,
  setUserName,
  setZl_UID,
} from '../../components/shared/getToken';
import CustomLoader from '../../components/shared/Loading';
import { notify } from '../../components/shared/notify';
import appwriteService, { setTokenInCookies } from '../../config/appwrite';
import { appWriteUser, useAuth } from '../../hooks/useAuth';
import { affiliateLinkClicks } from '../../services/Affiliate';
import {
  addCouponCode,
  addToCart,
  addToRecentlyViewed,
  getActiveCountries,
  getCartCount,
  getCartDetails,
} from '../../services/Cart';
import { createSecret } from '../../services/Library/Books';
import { getProductDetails } from '../../services/Products';
import { getUserProfile } from '../../services/user';
import { AppwriteUserData, User } from '../../utils/interfaces/User';

interface Props {
  product_id: string | string[] | undefined;
  redirect_url: string | string[] | undefined;
  afc: string | string[] | undefined;
  buy_now: string | string[] | undefined;
  cp: string | string[] | undefined;
  ref_code: string | string[] | undefined;
  deep_link_value: string | string[] | undefined;
}

export const STORAGE_KEY = 'zebra_selected_country';

const RedirectPage: React.FC<Props> = ({
  product_id,
  redirect_url,
  afc,
  buy_now,
  cp,
  ref_code,
  deep_link_value,
}) => {
  const [, setLocalCartCount] = useRecoilState(localCart);
  const [, setCartData] = useRecoilState(cartAtom);
  const [localUserId, setLocalUserId] = useRecoilState(localUserID);
  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [isCalled, setIsCalled] = useState<boolean>(false);
  const [isCountryReady, setIsCountryReady] = useState<boolean>(false);
  const setCountryState = useSetRecoilState(countryAtom);
  const { identifyUserWithPostHog } = useAuth();
  const checkTokenPromiseRef = useRef<Promise<User | undefined> | null>(null);

  const router = useRouter();
  const isBuyNow = buy_now?.toString() === 'true';

  const setCountry = useCallback(
    (country: any, source: 'manual' | 'auto') => {
      if (!country) return;

      setCountryState({
        isOpen: false,
        selectedCountry: {
          ...country,
          currency: country.currency || 'INR',
          currency_symbol: country.currency_symbol || '₹',
          timezone: country.timezone || 'Asia/Kolkata',
        },
        source,
      });

      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ country, source }));
        document.cookie = `country=${country.code}; path=/; max-age=${60 * 60 * 24
          }; SameSite=Lax`;
      }
    },
    [setCountryState],
  );

  const detectCountry = useCallback(
    async (countryList: any[], fallbackCountry: any) => {
      const sources = [
        {
          url: 'https://ipwho.is/',
          extract: (data: any) => ({
            code: data?.country_code,
            timezone: data?.timezone?.id || null,
          }),
        },
        {
          url: 'https://ipinfo.io/json',
          extract: (data: any) => ({
            code: data?.country,
            timezone: data?.timezone || null,
          }),
        },
      ];

      let detected = false;

      for (const source of sources) {
        try {
          const res = await fetch(source.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);

          const data = await res.json();
          const { code, timezone } = source.extract(data);
          if (!code) throw new Error('Missing country code');

          const found =
            countryList.find((c: any) => c.code === code) || fallbackCountry;

          if (found) {
            setCountry(
              { ...found, timezone: timezone || 'Asia/Kolkata' },
              'manual',
            );
            detected = true;
            setIsCountryReady(true);
            return;
          }
        } catch (err) {
          console.warn(`Country detection failed for ${source.url}`, err);
          continue;
        }
      }

      if (!detected) {
        setCountryState({
          isOpen: false,
          selectedCountry: fallbackCountry
            ? { ...fallbackCountry, timezone: 'Asia/Kolkata' }
            : null,
          source: 'manual',
        });
      }

      setIsCountryReady(true);
    },
    [setCountry, setCountryState],
  );

  useEffect(() => {
    (async () => {
      if (!router.isReady) return;

      const cn = router.query?.cn as string | undefined;

      try {
        const countryList = await getActiveCountries();
        const fallbackCountry =
          countryList.find((c: any) => c.code === 'IN') || null;

        if (cn) {
          const matchedCountry =
            countryList.find(
              (country: any) => country.code.toUpperCase() === cn.toUpperCase(),
            ) || fallbackCountry;

          if (matchedCountry) {
            setCountry(
              {
                ...matchedCountry,
                timezone: matchedCountry.timezone || 'Asia/Kolkata',
              },
              'manual',
            );

            localStorage.setItem(
              STORAGE_KEY,
              JSON.stringify({ country: matchedCountry, source: 'manual' }),
            );
            document.cookie = `country=${matchedCountry.code}; path=/; max-age=${60 * 60 * 24
              }; SameSite=Lax`;

            setIsCountryReady(true);
            return;
          }
        }

        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (parsed?.country) {
              setCountry(parsed.country, parsed.source || 'manual');
              setIsCountryReady(true);
              return;
            }
          } catch {
            localStorage.removeItem(STORAGE_KEY);
          }
        }

        await detectCountry(countryList, fallbackCountry);
      } catch (err) {
        console.error('Country detection failed:', err);
        setIsCountryReady(true);
      }
    })();
  }, [router.isReady, detectCountry, setCountry]);

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged]);

  useEffect(() => {
    if (!userData) {
      getUserProfile()
        .then((res) => {
          if (res?.data) {
            setUserData(res.data);
          }
        })
        .catch(() => { });
    }
  }, [userData]);

  const handleCartCount = async (cartId?: number) => {
    try {
      const data = await getCartCount({ cartId });
      setLocalCartCount(data.count);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const handleCartDetails = async (cartId?: number) => {
    try {
      const data = await getCartDetails(localUserId.toString(), cartId);

      const formattedData = formatCartData(
        data?.data?.[0]?.cart_items,
        data?.currency_code,
        data?.cart_id,
      );
      setCartData(formattedData);
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const handleCreateSecret = async (
    token: string | null,
  ): Promise<User | undefined> => {
    try {
      if (token) {
        const response = await createSecret();
        if (response?.zl_uid) {
          const responseAppwriteSession =
            await appwriteService.createSessionBySecretKey({
              user_id: response?.zl_uid,
              secret: response?.secret,
            });
          Cookies.remove('courseRedirectCookieToken');
          if (responseAppwriteSession) {
            const user = await appwriteService.getCurrentUser();
            let currentUser: AppwriteUserData | undefined = undefined;
            const token = await setTokenInCookies();
            if (user !== null) {
              currentUser = user as unknown as AppwriteUserData;
            }
            const dbUser: User = await appWriteUser(
              currentUser?.userId as string,
            );
            identifyUserWithPostHog({
              id: dbUser?.zl_uid as string,
              email: dbUser?.email,
              name: dbUser?.name,
            });
            await setToken(token as string);
            await setUserName(dbUser?.name);
            setUserData(dbUser);
            setIsTokenChanged(true);
            await setUserId(dbUser?.id?.toString());
            await setZl_UID(dbUser?.zl_uid?.toString() as string);
            return dbUser;
          }
        }
      }
    } catch (error) {
      console.error('Invalid token', error);
    }
    return undefined;
  };

  const checkToken = async (): Promise<User | undefined> => {
    if (!redirect_url || !router.isReady) return undefined;
    if (checkTokenPromiseRef.current) return checkTokenPromiseRef.current;

    checkTokenPromiseRef.current = (async () => {
      const CRYPTO_KEY = process.env.NEXT_PUBLIC_CRYPTO_KEY || '';
      const encrypt = encodeURIComponent(redirect_url as string);
      const decryptedToken = decryptToken(encrypt, CRYPTO_KEY);
      Cookies.set('courseRedirectCookieToken', decryptedToken as string);
      return await handleCreateSecret(decryptedToken);
    })();

    return checkTokenPromiseRef.current;
  };

  const resolveUserData = async (): Promise<User | undefined> => {
    if (userData) return userData;

    if (redirect_url && router.isReady) {
      const userFromToken = await checkToken();
      if (userFromToken) return userFromToken;
    }

    try {
      const profile = await getUserProfile();
      if (profile?.data) {
        setUserData(profile.data);
        if (profile.data.id) await setUserId(profile.data.id.toString());
        if (profile.data.zl_uid)
          await setZl_UID(profile.data.zl_uid.toString());
        return profile.data;
      }
    } catch {
      // Not logged in or fetch failed
    }

    const [zl_uid, userId, name] = await Promise.all([
      getZl_UID(),
      getUserId(),
      getUserName(),
    ]);

    if (zl_uid || userId) {
      const fallbackUser = {
        zl_uid: zl_uid || '',
        id: userId ? Number(userId) : undefined,
        name: name || '',
      } as User;
      return fallbackUser;
    }

    return undefined;
  };

  const processCartItems = async () => {
    try {
      const currentUser = await resolveUserData();
      const items = (product_id as string)?.split(',') || [];
      let cartIdLocal;

      const effectiveLocalUserId =
        localUserId ||
        (typeof window !== 'undefined' ? localStorage.getItem('userId') : '') ||
        '';

      for (const item of items) {
        let productInfo: any = null;
        try {
          const productRes = await getProductDetails(item);
          productInfo = productRes?.data || productRes;
        } catch (err) {
          console.error('Error fetching product details for item', item, err);
        }

        console.log({ productInfo })

        const heading =
          productInfo?.display_name ||
          productInfo?.name ||
          (router.query?.slug as string)?.replace(/-/g, ' ') ||
          'Book';

        const category =
          (typeof productInfo?.category === 'string'
            ? productInfo?.category
            : productInfo?.category?.name) ||
          'books';

        const price =
          productInfo?.i18n_attributes?.unit_price ??
          productInfo?.unit_price ??
          productInfo?.main_price ??
          productInfo?.price ??
          0;

        const currency =
          productInfo?.i18n_attributes?.currency ||
          productInfo?.currency ||
          'INR';

        console.log({ userData: currentUser, productInfo });
        const data = await addToCart({
          id: Number(item),
          temp_user_id: effectiveLocalUserId ? effectiveLocalUserId.toString() : '',
          quantity: 1,
          isBuyNow,
          trackingData: {
            productData: {
              price: Number(price),
              id: Number(item),
              heading,
              quantity: 1,
              currency,
              category,
            },
            userData: currentUser,
            pathname: router.pathname,
            productType: 'hardcopy',
            type: 'books',
          },
        });
        cartIdLocal = data?.data?.cart_id;
        addToRecentlyViewed(Number(item), currentUser?.zl_uid);
      }

      if (cp) handleApplyCoupon(cp as string);

      handleCartCount(isBuyNow && cartIdLocal);

      if (effectiveLocalUserId) handleCartDetails(isBuyNow && cartIdLocal);

      if (items.length > 0 && router.isReady) {
        if (isBuyNow) {
          // Forward the `cn` param so the checkout page can apply the correct country
          // even when the URL is shared in another browser / incognito session.
          const cnParam = router.query?.cn as string | undefined;
          const checkoutUrl = `${process.env.NEXT_PUBLIC_CHECKOUT_ROUTE || '/checkout'}?cartId=${cartIdLocal}&buy_now=true${cnParam ? `&cn=${cnParam}` : ''}`;
          router.push(checkoutUrl);
        } else router.push(`/cart/`);
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const setUserIdInLocalStorage = async () => {
    const existingUserId = localStorage.getItem('userId');
    if (!existingUserId) {
      const userId = uuidv4();
      localStorage.setItem('userId', userId);
      setTimeout(() => {
        setLocalCartCount(0);
        removeUserId();
        removeZl_UID();
      }, 2000);
      setLocalUserId(userId);
    } else {
      setLocalUserId(existingUserId as string);
    }
  };

  useEffect(() => {
    if (router.isReady && isCountryReady) {
      if (product_id && !isCalled) {
        processCartItems();
        setIsCalled(true);
      }

      if (afc && !loggedIn) {
        handleLinkClicks(afc as string);
        Cookies.set('afc', afc as string, { expires: 7 });
      }
      if (ref_code)
        Cookies.set('ref_code', ref_code as string, { expires: 30 });
      if (deep_link_value)
        Cookies.set('ref_code', deep_link_value as string, { expires: 30 });
    }
  }, [
    isCountryReady,
    product_id,
    localUserId,
    router,
    afc,
    isCalled,
    ref_code,
    deep_link_value,
    loggedIn,
  ]);

  useEffect(() => {
    if (redirect_url && router.isReady) checkToken();
    else setUserIdInLocalStorage();
  }, [redirect_url, router]);

  const handleLinkClicks = async (afc: string) => {
    try {
      await affiliateLinkClicks(afc);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleApplyCoupon = async (coupon: string) => {
    try {
      await addCouponCode(coupon as string);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  return <CustomLoader />;
};

export async function getServerSideProps(context: any) {
  const { query } = context;
  const product_id = query['add-to-cart'] || null;
  const afc = query['afc'] || '';
  const ref_code = query['ref_code'] || '';
  const deep_link_value = query['deep_link_value'] || '';
  const buy_now = query['buy_now'] || '';
  const cp = query['cp'] || null;
  const redirect_url =
    query.redirect_url !== undefined ? query.redirect_url : null;

  const hasActionQuery = Boolean(
    product_id || redirect_url || afc || ref_code || deep_link_value,
  );

  if (!hasActionQuery) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      product_id,
      redirect_url,
      afc,
      buy_now,
      cp,
      ref_code,
      deep_link_value,
    },
  };
}

export default RedirectPage;
